/**
 * Placeholder Helper Panel for GrapesJS
 *
 * Provides a helper panel with:
 * - Grouped, collapsible placeholder sections
 * - Database placeholders ({{db.*}})
 * - Recipient placeholders ({{recipient.*}})
 * - Meta placeholders ({{meta.*}})
 * - Copy-to-clipboard functionality
 * - Search/filter
 */
export default class PlaceholderHelper {
    constructor(editor, options = {}) {
        this.editor = editor;
        this.options = options;
        this.codeEditorMode = options.codeEditorMode || false;
        this.codeEditor = options.codeEditor || null;
        this.placeholders = this.getPlaceholderDefinitions();
    }

    /**
     * Get all available placeholder definitions organized by groups
     */
    getPlaceholderDefinitions() {
        return {
            products: {
                label: 'Products',
                description: 'Product data from database (compile-time)',
                color: '#4CAF50',
                collapsed: true,
                placeholders: [
                    {
                        placeholder: '{{db.primary.products.{ID}.name.en}}',
                        description: 'Product name (English)',
                        example: '{{db.primary.products.ABC123.name.en}}'
                    },
                    {
                        placeholder: '{{db.primary.products.{ID}.name.fr}}',
                        description: 'Product name (French)',
                        example: '{{db.primary.products.ABC123.name.fr}}'
                    },
                    {
                        placeholder: '{{db.primary.products.{ID}.handle}}',
                        description: 'Product URL handle/slug',
                        example: '{{db.primary.products.ABC123.handle}}'
                    },
                    {
                        placeholder: '{{db.primary.products.{ID}.price}}',
                        description: 'Product price',
                        example: '{{db.primary.products.ABC123.price}}'
                    },
                    {
                        placeholder: '{{db.primary.products.{ID}.description.en}}',
                        description: 'Product description (English)',
                        example: '{{db.primary.products.ABC123.description.en}}'
                    },
                    {
                        placeholder: '{{db.primary.products.{ID}.description.fr}}',
                        description: 'Product description (French)',
                        example: '{{db.primary.products.ABC123.description.fr}}'
                    },
                    {
                        placeholder: '{{db.primary.products.{ID}.type}}',
                        description: 'Product type/category',
                        example: '{{db.primary.products.ABC123.type}}'
                    },
                    {
                        placeholder: '{{db.primary.products.{ID}.images.[0].url}}',
                        description: 'Product image URL (first)',
                        example: '{{db.primary.products.ABC123.images.[0].url}}'
                    },
                    {
                        placeholder: '{{db.primary.products.{ID}.images.[0].alt}}',
                        description: 'Product image alt text (first)',
                        example: '{{db.primary.products.ABC123.images.[0].alt}}'
                    }
                ]
            },
            recommendations: {
                label: 'Recommendations',
                description: 'Per-recipient recommended products injected by the AI send pipeline (send-time, repeating). Lay out a grid with indexed slots [0]–[8]; each card reuses the same fields with a different INDEX.',
                color: '#E91E63',
                collapsed: true,
                placeholders: [
                    {
                        placeholder: '{{products.[{INDEX}].image}}',
                        description: 'Recommended product image (price / % discount / PLUS price are baked into the image)',
                        example: '{{products.[0].image}}'
                    },
                    {
                        placeholder: '{{products.[{INDEX}].url}}',
                        description: 'Recommended product link',
                        example: '{{products.[0].url}}'
                    },
                    {
                        placeholder: '{{products.[{INDEX}].title}}',
                        description: 'Recommended product title',
                        example: '{{products.[0].title}}'
                    },
                    {
                        placeholder: '{{products.[{INDEX}].price}}',
                        description: 'Recommended product price (text)',
                        example: '{{products.[0].price}}'
                    },
                    {
                        placeholder: '{{products.[{INDEX}].discount_price}}',
                        description: 'Recommended product sale price (present only when on sale)',
                        example: '{{products.[0].discount_price}}'
                    }
                ]
            },
            users: {
                label: 'Users',
                description: 'User data from database (compile-time)',
                color: '#9C27B0',
                collapsed: true,
                placeholders: [
                    {
                        placeholder: '{{db.primary.users.{ID}.firstName}}',
                        description: 'User first name',
                        example: '{{db.primary.users.USER001.firstName}}'
                    },
                    {
                        placeholder: '{{db.primary.users.{ID}.lastName}}',
                        description: 'User last name',
                        example: '{{db.primary.users.USER001.lastName}}'
                    },
                    {
                        placeholder: '{{db.primary.users.{ID}.email}}',
                        description: 'User email',
                        example: '{{db.primary.users.USER001.email}}'
                    }
                ]
            },
            recipient: {
                label: 'Recipients',
                description: 'Recipient data injected by SendGrid (send-time)',
                color: '#2196F3',
                collapsed: true,
                placeholders: [
                    {
                        placeholder: '{{recipient.firstName}}',
                        description: 'Recipient first name',
                        example: '{{recipient.firstName}}'
                    },
                    {
                        placeholder: '{{recipient.lastName}}',
                        description: 'Recipient last name',
                        example: '{{recipient.lastName}}'
                    },
                    {
                        placeholder: '{{recipient.email}}',
                        description: 'Recipient email address',
                        example: '{{recipient.email}}'
                    },
                    {
                        placeholder: '{{recipient.company}}',
                        description: 'Recipient company name',
                        example: '{{recipient.company}}'
                    },
                    {
                        placeholder: '{{recipient.city}}',
                        description: 'Recipient city',
                        example: '{{recipient.city}}'
                    },
                    {
                        placeholder: '{{recipient.state}}',
                        description: 'Recipient state',
                        example: '{{recipient.state}}'
                    },
                    {
                        placeholder: '{{recipient.country}}',
                        description: 'Recipient country',
                        example: '{{recipient.country}}'
                    }
                ]
            },
            meta: {
                label: 'Metadata',
                description: 'Email metadata injected by SendGrid (send-time)',
                color: '#FF9800',
                collapsed: true,
                placeholders: [
                    {
                        placeholder: '{{meta.unsubscribeLink}}',
                        description: 'Unsubscribe link',
                        example: '{{meta.unsubscribeLink}}'
                    },
                    {
                        placeholder: '{{meta.sendDate}}',
                        description: 'Email send date',
                        example: '{{meta.sendDate}}'
                    },
                    {
                        placeholder: '{{subject}}',
                        description: 'Email subject (dynamic)',
                        example: '{{subject}}'
                    }
                ]
            }
        };
    }

    /**
     * Initialize the placeholder helper panel
     */
    init() {
        try {
            this.addPanel();
            this.addStyles();
        } catch (error) {
            console.error('PlaceholderHelper: Initialization failed', error);
        }
    }

    /**
     * Add the placeholder helper panel to GrapesJS
     */
    addPanel() {
        try {
            const editor = this.editor;

            if (!editor || !editor.Panels) {
                console.error('PlaceholderHelper: Editor or Panels not available');
                return;
            }

            // Add panel button to options panel (top-right toolbar)
            const panelManager = editor.Panels;

            panelManager.addButton('options', {
                id: 'show-placeholders',
                className: 'fa fa-tags',
                command: 'show-placeholders',
                attributes: { title: 'Placeholder Helper' },
                active: false,
            });

            editor.Commands.add('show-placeholders', {
                run: (editor) => this.showPanel(),
                stop: (editor) => this.hidePanel(),
            });

        } catch (error) {
            console.error('PlaceholderHelper: Failed to add panel', error);
        }
    }

    /**
     * Show the placeholder helper panel
     * @param {Object} [opts] - Options: { fromCodeEditor: boolean, codeEditor: CodeMirror }
     */
    showPanel(opts = {}) {
        // Track whether opened from code editor (for insert button visibility)
        this._openedFromCodeEditor = !!(opts.fromCodeEditor);
        if (opts.codeEditor) {
            this.codeEditor = opts.codeEditor;
            this.codeEditorMode = true;
        }

        // Always recreate panel to reflect current insert-button state
        let panel = document.getElementById('placeholder-helper-panel');
        if (panel) {
            panel.remove();
        }

        panel = this.createPanel();
        if (!document.body || !(panel instanceof Node)) {
            console.error('PlaceholderHelper: Cannot create panel');
            return;
        }
        document.body.appendChild(panel);
        panel.style.display = 'block';

        // Close button
        const closeBtn = panel.querySelector('.placeholder-helper-close');
        if (closeBtn) {
            closeBtn.onclick = () => this.hidePanel();
        }

        // Search
        const searchInput = panel.querySelector('.placeholder-search');
        if (searchInput) {
            searchInput.oninput = (e) => this.filterPlaceholders(e.target.value);
        }

        // Copy buttons — show parameter dialog when placeholder has {ID} fields
        const copyButtons = panel.querySelectorAll('.placeholder-copy-btn');
        copyButtons.forEach(btn => {
            btn.onclick = (e) => {
                e.preventDefault();
                const placeholder = btn.getAttribute('data-placeholder');
                const params = this.detectParameters(placeholder);
                if (params.length > 0) {
                    // Has parameters — show dialog, then copy result
                    this.showParameterDialog(params, placeholder, btn, 'copy');
                } else {
                    this.copyToClipboard(placeholder);
                    this.showCopyFeedback(btn);
                }
            };
        });

        // Insert buttons
        const insertButtons = panel.querySelectorAll('.placeholder-insert-btn');
        insertButtons.forEach(btn => {
            btn.onclick = (e) => {
                e.preventDefault();
                const placeholder = btn.getAttribute('data-placeholder');
                const example = btn.getAttribute('data-example');
                this.handleInsert(placeholder, example, btn);
            };
        });

        // Section collapse/expand
        const sectionHeaders = panel.querySelectorAll('.placeholder-section-header');
        sectionHeaders.forEach(header => {
            header.onclick = () => this.toggleSection(header);
        });

        // (1) Close when clicking outside the panel
        setTimeout(() => {
            this._outsideClickHandler = (e) => {
                // Don't close if clicking inside the panel or a param dialog
                if (panel.contains(e.target) || e.target.closest('.placeholder-param-modal')) {
                    return;
                }
                // Don't close if clicking the toolbar button that toggles the panel
                if (e.target.closest('.fa-tags') || e.target.closest('[data-command="show-placeholders"]')) {
                    return;
                }
                this.hidePanel();
            };
            document.addEventListener('mousedown', this._outsideClickHandler, true);
        }, 100); // Small delay so the opening click doesn't immediately close it
    }

    /**
     * Hide the placeholder helper panel and deselect the toolbar button
     */
    hidePanel() {
        const panel = document.getElementById('placeholder-helper-panel');
        if (panel) {
            panel.style.display = 'none';
        }

        // Remove outside-click listener
        if (this._outsideClickHandler) {
            document.removeEventListener('mousedown', this._outsideClickHandler, true);
            this._outsideClickHandler = null;
        }

        // (2) Deselect the toolbar button
        try {
            const editor = this.editor;
            if (editor && editor.Commands) {
                editor.Commands.stop('show-placeholders');
            }
            // Also visually deactivate the button
            const btn = document.querySelector('.gjs-pn-btn.fa-tags');
            if (btn) {
                btn.classList.remove('gjs-pn-active');
            }
        } catch (e) {
            // Ignore
        }
    }

    /**
     * Toggle collapse/expand of a section
     */
    toggleSection(header) {
        const section = header.closest('.placeholder-section');
        const list = section.querySelector('.placeholder-list');
        const icon = header.querySelector('.collapse-icon');

        if (list && icon) {
            const isCollapsed = list.style.display === 'none';

            if (isCollapsed) {
                list.style.display = 'block';
                icon.style.transform = 'rotate(0deg)';
                section.classList.remove('collapsed');
            } else {
                list.style.display = 'none';
                icon.style.transform = 'rotate(-90deg)';
                section.classList.add('collapsed');
            }
        }
    }

    /**
     * Create the placeholder panel HTML
     */
    createPanel() {
        const panel = document.createElement('div');
        panel.id = 'placeholder-helper-panel';
        panel.className = 'placeholder-helper-panel';

        let html = `
            <div class="placeholder-helper-header">
                <h3>📌 Placeholder Helper</h3>
                <button class="placeholder-helper-close" title="Close">&times;</button>
            </div>

            <div class="placeholder-helper-search-container">
                <input
                    type="text"
                    class="placeholder-search"
                    placeholder="Search placeholders..."
                    aria-label="Search placeholders"
                />
            </div>

            <div class="placeholder-helper-content">
        `;

        // Add sections for each placeholder type
        Object.keys(this.placeholders).forEach(key => {
            const section = this.placeholders[key];
            html += this.createSection(key, section);
        });

        html += `
            </div>
            <div class="placeholder-helper-footer">
                <small>
                    <strong>Tip:</strong> Click any placeholder to copy it to clipboard. Click section headers to expand/collapse.
                </small>
            </div>
        `;

        panel.innerHTML = html;
        return panel;
    }

    /**
     * Create a collapsible section for a placeholder group
     */
    createSection(key, section) {
        const isCollapsed = section.collapsed ? 'collapsed' : '';
        const listDisplay = section.collapsed ? 'style="display: none;"' : '';
        const iconRotation = section.collapsed ? 'style="transform: rotate(-90deg);"' : '';

        let html = `
            <div class="placeholder-section ${isCollapsed}" data-section="${key}">
                <div class="placeholder-section-header" style="border-left: 4px solid ${section.color}">
                    <div class="section-header-content">
                        <div class="section-title-row">
                            <i class="fa fa-chevron-down collapse-icon" ${iconRotation}></i>
                            <h4>${section.label}</h4>
                            <span class="placeholder-count">${section.placeholders.length}</span>
                        </div>
                        <p class="placeholder-section-description">${section.description}</p>
                    </div>
                </div>
                <div class="placeholder-list" ${listDisplay}>
        `;

        section.placeholders.forEach(item => {
            // (3) Only show Insert buttons when opened from code editor button
            const showInsertButton = this._openedFromCodeEditor;

            html += `
                <div class="placeholder-item" data-placeholder="${item.example}">
                    <div class="placeholder-info">
                        <code class="placeholder-code">${item.example}</code>
                        <span class="placeholder-description">${item.description}</span>
                    </div>
                    <div class="placeholder-actions">
                        ${showInsertButton ? `
                        <button
                            class="placeholder-insert-btn"
                            data-placeholder="${item.placeholder}"
                            data-example="${item.example}"
                            title="Insert into editor"
                        >
                            <i class="fa fa-plus"></i> Insert
                        </button>
                        ` : ''}
                        <button
                            class="placeholder-copy-btn"
                            data-placeholder="${item.placeholder}"
                            title="Copy to clipboard"
                        >
                            <i class="fa fa-copy"></i>
                        </button>
                    </div>
                </div>
            `;
        });

        html += `
                </div>
            </div>
        `;

        return html;
    }

    /**
     * Filter placeholders based on search query
     */
    filterPlaceholders(query) {
        const lowerQuery = query.toLowerCase();
        const items = document.querySelectorAll('.placeholder-item');

        let visibleCount = 0;

        items.forEach(item => {
            const code = item.querySelector('.placeholder-code').textContent.toLowerCase();
            const description = item.querySelector('.placeholder-description').textContent.toLowerCase();

            if (code.includes(lowerQuery) || description.includes(lowerQuery)) {
                item.style.display = 'flex';
                visibleCount++;
            } else {
                item.style.display = 'none';
            }
        });

        // Show/hide sections based on visible items and expand sections with matches
        const sections = document.querySelectorAll('.placeholder-section');
        sections.forEach(section => {
            const list = section.querySelector('.placeholder-list');
            const visibleItems = list.querySelectorAll('.placeholder-item:not([style*="display: none"])');

            if (visibleItems.length > 0) {
                section.style.display = 'block';
                // Auto-expand sections with search matches
                if (query) {
                    list.style.display = 'block';
                    const icon = section.querySelector('.collapse-icon');
                    if (icon) icon.style.transform = 'rotate(0deg)';
                    section.classList.remove('collapsed');
                }
            } else {
                section.style.display = 'none';
            }
        });
    }

    /**
     * Copy text to clipboard
     */
    copyToClipboard(text) {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text).catch(err => {
                console.error('Failed to copy:', err);
                this.fallbackCopy(text);
            });
        } else {
            this.fallbackCopy(text);
        }
    }

    /**
     * Fallback copy method for older browsers
     */
    fallbackCopy(text) {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        try {
            document.execCommand('copy');
        } catch (err) {
            console.error('Fallback copy failed:', err);
        }
        document.body.removeChild(textarea);
    }

    /**
     * Show visual feedback when copying
     */
    showCopyFeedback(button) {
        const originalHTML = button.innerHTML;
        button.innerHTML = '<i class="fa fa-check"></i>';
        button.classList.add('copied');

        setTimeout(() => {
            button.innerHTML = originalHTML;
            button.classList.remove('copied');
        }, 1500);
    }

    /**
     * Handle insert button click - detect parameters and show dialog if needed
     */
    handleInsert(placeholder, example, button) {
        const params = this.detectParameters(placeholder);

        if (params.length > 0) {
            // Has parameters - show input dialog
            this.showParameterDialog(params, placeholder, button);
        } else {
            // No parameters - insert directly
            this.insertIntoEditor(placeholder);
            this.showInsertFeedback(button);
        }
    }

    /**
     * Detect parameters in placeholder like {ID} or [0]
     * Returns array of parameter objects with name, type, and placeholder
     */
    detectParameters(placeholder) {
        const params = [];

        // Match {ID}, {INDEX}, or any {WORD} pattern
        const curlyMatches = placeholder.matchAll(/\{([A-Z]+)\}/g);
        for (const match of curlyMatches) {
            const paramName = match[1];
            params.push({
                name: paramName,
                type: 'curly',
                placeholder: match[0],
                label: paramName.charAt(0) + paramName.slice(1).toLowerCase(),
                example: this.getParameterExample(paramName)
            });
        }

        // Match [0], [1], etc. for array indices
        const bracketMatches = placeholder.matchAll(/\[(\d+)\]/g);
        for (const match of bracketMatches) {
            const index = match[1];
            params.push({
                name: `INDEX_${index}`,
                type: 'bracket',
                placeholder: match[0],
                label: `Array Index (currently ${index})`,
                example: index
            });
        }

        return params;
    }

    /**
     * Get example value for a parameter type
     */
    getParameterExample(paramName) {
        const examples = {
            'ID': 'ABC123',
            'INDEX': '0'
        };
        return examples[paramName] || 'value';
    }

    /**
     * Show parameter input dialog
     * @param {Array} params - Parameters to fill
     * @param {string} placeholder - Placeholder template
     * @param {HTMLElement} button - The button that triggered this
     * @param {string} [action='insert'] - 'insert' or 'copy'
     */
    showParameterDialog(params, placeholder, button, action = 'insert') {
        const isCopy = action === 'copy';
        const actionLabel = isCopy ? 'Copy' : 'Insert';
        const actionIcon = isCopy ? 'fa-copy' : 'fa-plus';
        const actionColor = isCopy ? '#667eea' : '#4caf50';

        // Create modal overlay
        const modal = document.createElement('div');
        modal.className = 'placeholder-param-modal';
        modal.innerHTML = `
            <div class="placeholder-param-modal-content">
                <div class="placeholder-param-modal-header" style="background: linear-gradient(135deg, ${actionColor} 0%, ${actionColor}dd 100%);">
                    <h4>Fill in Parameters</h4>
                    <button class="placeholder-param-close" title="Close">&times;</button>
                </div>
                <div class="placeholder-param-modal-body">
                    <p class="param-instruction">This placeholder requires the following parameters:</p>
                    <form id="param-form">
                        ${params.map((param, index) => `
                            <div class="param-field">
                                <label for="param-${index}">${param.label}:</label>
                                <input
                                    type="text"
                                    id="param-${index}"
                                    name="param-${index}"
                                    placeholder="${param.example}"
                                    required
                                    autocomplete="off"
                                />
                                <small>Replaces <code>${param.placeholder}</code></small>
                            </div>
                        `).join('')}
                    </form>
                    <div class="param-preview">
                        <label>Preview:</label>
                        <code id="param-preview-code">${placeholder}</code>
                    </div>
                </div>
                <div class="placeholder-param-modal-footer">
                    <button type="button" class="param-cancel-btn">Cancel</button>
                    <button type="submit" class="param-insert-btn" form="param-form" style="background: ${actionColor};">
                        <i class="fa ${actionIcon}"></i> ${actionLabel}
                    </button>
                </div>
            </div>
        `;

        document.body.appendChild(modal);

        // Add event listeners
        const closeBtn = modal.querySelector('.placeholder-param-close');
        const cancelBtn = modal.querySelector('.param-cancel-btn');
        const form = modal.querySelector('#param-form');
        const inputs = modal.querySelectorAll('input[type="text"]');
        const previewCode = modal.querySelector('#param-preview-code');

        // Close handlers
        const closeModal = () => {
            modal.remove();
        };

        closeBtn.onclick = closeModal;
        cancelBtn.onclick = closeModal;
        modal.onclick = (e) => {
            if (e.target === modal) closeModal();
        };

        // Live preview update
        inputs.forEach((input, index) => {
            input.oninput = () => {
                let preview = placeholder;
                inputs.forEach((inp, idx) => {
                    const value = inp.value.trim() || params[idx].example;
                    preview = preview.replace(params[idx].placeholder, value);
                });
                previewCode.textContent = preview;
            };

            // Focus first input
            if (index === 0) {
                setTimeout(() => input.focus(), 100);
            }
        });

        // Form submit
        form.onsubmit = (e) => {
            e.preventDefault();

            // Build final placeholder with user values
            let finalPlaceholder = placeholder;
            params.forEach((param, index) => {
                const input = document.getElementById(`param-${index}`);
                const value = input.value.trim();
                if (value) {
                    finalPlaceholder = finalPlaceholder.replace(param.placeholder, value);
                }
            });

            if (isCopy) {
                // Copy mode: copy to clipboard
                this.copyToClipboard(finalPlaceholder);
                this.showCopyFeedback(button);
            } else {
                // Insert mode: insert into editor
                this.insertIntoEditor(finalPlaceholder);
                this.showInsertFeedback(button);
            }
            closeModal();
        };
    }

    /**
     * Insert text into editor at cursor position (GrapesJS or CodeMirror)
     */
    insertIntoEditor(text) {
        try {
            // Code editor mode - insert into CodeMirror
            if (this.codeEditorMode && this.codeEditor) {
                const cm = this.codeEditor;
                const cursor = cm.getCursor();
                cm.replaceRange(text, cursor);

                // Move cursor to end of inserted text
                const newCursor = {
                    line: cursor.line,
                    ch: cursor.ch + text.length
                };
                cm.setCursor(newCursor);
                cm.focus();

                this.showNotification('Placeholder inserted successfully!', 'success');
                return;
            }

            // GrapesJS mode - insert into visual editor
            const editor = this.editor;

            // Get selected component
            const selected = editor.getSelected();

            if (selected && selected.is('text')) {
                // If it's a text component, insert at the end
                const currentContent = selected.get('content') || '';
                selected.set('content', currentContent + text);
            } else {
                // Try to insert into RTE if active
                const rte = editor.RichTextEditor;
                if (rte && rte.isActive()) {
                    rte.insertHTML(text);
                } else {
                    // Fallback: add as new text component
                    const wrapper = editor.getWrapper();
                    if (wrapper) {
                        wrapper.append({
                            type: 'text',
                            content: text
                        });
                    }
                }
            }

            // Show success notification
            this.showNotification('Placeholder inserted successfully!', 'success');
        } catch (error) {
            console.error('PlaceholderHelper: Failed to insert into editor', error);
            this.showNotification('Failed to insert placeholder. Please try copying instead.', 'error');
        }
    }

    /**
     * Show visual feedback when inserting
     */
    showInsertFeedback(button) {
        const originalHTML = button.innerHTML;
        button.innerHTML = '<i class="fa fa-check"></i> Inserted';
        button.classList.add('inserted');

        setTimeout(() => {
            button.innerHTML = originalHTML;
            button.classList.remove('inserted');
        }, 2000);
    }

    /**
     * Show notification message
     */
    showNotification(message, type = 'success') {
        const notification = document.createElement('div');
        notification.className = `placeholder-notification placeholder-notification-${type}`;
        notification.innerHTML = `
            <i class="fa fa-${type === 'success' ? 'check-circle' : 'exclamation-circle'}"></i>
            <span>${message}</span>
        `;

        document.body.appendChild(notification);

        // Animate in
        setTimeout(() => notification.classList.add('show'), 10);

        // Remove after 3 seconds
        setTimeout(() => {
            notification.classList.remove('show');
            setTimeout(() => notification.remove(), 300);
        }, 3000);
    }

    /**
     * Add CSS styles for the placeholder panel
     */
    addStyles() {
        const styleId = 'placeholder-helper-styles';

        // Check if styles already added
        if (document.getElementById(styleId)) {
            return;
        }

        // Safety check for document.head
        if (!document.head) {
            console.error('PlaceholderHelper: document.head not available');
            return;
        }

        try {
            const style = document.createElement('style');

            // Verify style element was created successfully
            if (!style || !(style instanceof Node)) {
                console.error('PlaceholderHelper: Failed to create style element');
                return;
            }

            style.id = styleId;
            style.textContent = `
            .placeholder-helper-panel {
                position: fixed;
                top: 80px;
                right: 20px;
                width: 500px;
                max-height: 85vh;
                background: white;
                border: 1px solid #ddd;
                border-radius: 8px;
                box-shadow: 0 4px 16px rgba(0,0,0,0.15);
                z-index: 10000;
                display: none;
                flex-direction: column;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            }

            .placeholder-helper-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 16px 20px;
                border-bottom: 1px solid #eee;
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                color: white;
                border-radius: 8px 8px 0 0;
            }

            .placeholder-helper-header h3 {
                margin: 0;
                font-size: 18px;
                font-weight: 600;
            }

            .placeholder-helper-close {
                background: none;
                border: none;
                color: white;
                font-size: 28px;
                cursor: pointer;
                padding: 0;
                width: 32px;
                height: 32px;
                line-height: 1;
                border-radius: 4px;
                transition: background 0.2s;
            }

            .placeholder-helper-close:hover {
                background: rgba(255,255,255,0.2);
            }

            .placeholder-helper-search-container {
                padding: 16px 20px;
                border-bottom: 1px solid #eee;
                background: #f9f9f9;
            }

            .placeholder-search {
                width: 100%;
                padding: 10px 12px;
                border: 1px solid #ddd;
                border-radius: 6px;
                font-size: 14px;
                outline: none;
                transition: border-color 0.2s;
            }

            .placeholder-search:focus {
                border-color: #667eea;
            }

            .placeholder-helper-content {
                overflow-y: auto;
                max-height: calc(85vh - 200px);
                padding: 0;
            }

            .placeholder-section {
                margin-bottom: 0;
                border-bottom: 1px solid #eee;
            }

            .placeholder-section:last-child {
                border-bottom: none;
            }

            .placeholder-section-header {
                padding: 14px 20px;
                background: #fafafa;
                cursor: pointer;
                transition: background 0.2s;
                user-select: none;
            }

            .placeholder-section-header:hover {
                background: #f0f0f0;
            }

            .section-header-content {
                display: flex;
                flex-direction: column;
                gap: 4px;
            }

            .section-title-row {
                display: flex;
                align-items: center;
                gap: 10px;
            }

            .collapse-icon {
                font-size: 12px;
                color: #666;
                transition: transform 0.2s ease;
                flex-shrink: 0;
            }

            .placeholder-section-header h4 {
                margin: 0;
                font-size: 15px;
                font-weight: 600;
                color: #333;
                flex: 1;
            }

            .placeholder-count {
                background: #667eea;
                color: white;
                font-size: 11px;
                font-weight: 600;
                padding: 2px 8px;
                border-radius: 12px;
                min-width: 22px;
                text-align: center;
            }

            .placeholder-section-description {
                margin: 0 0 0 22px;
                font-size: 12px;
                color: #666;
            }

            .placeholder-list {
                padding: 0;
                transition: all 0.2s ease;
            }

            .placeholder-item {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 12px 20px 12px 32px;
                border-bottom: 1px solid #f0f0f0;
                transition: background 0.2s;
            }

            .placeholder-item:hover {
                background: #f5f7ff;
            }

            .placeholder-item:last-child {
                border-bottom: none;
            }

            .placeholder-info {
                flex: 1;
                min-width: 0;
            }

            .placeholder-code {
                display: block;
                font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
                font-size: 12px;
                color: #d73a49;
                background: #f6f8fa;
                padding: 4px 8px;
                border-radius: 4px;
                margin-bottom: 4px;
                word-break: break-all;
            }

            .placeholder-description {
                display: block;
                font-size: 12px;
                color: #666;
            }

            .placeholder-actions {
                display: flex;
                gap: 6px;
                flex-shrink: 0;
                margin-left: 12px;
            }

            .placeholder-insert-btn {
                flex-shrink: 0;
                padding: 8px 12px;
                background: #4caf50;
                color: white;
                border: none;
                border-radius: 6px;
                cursor: pointer;
                font-size: 12px;
                font-weight: 500;
                transition: all 0.2s;
                white-space: nowrap;
            }

            .placeholder-insert-btn:hover {
                background: #45a049;
                transform: scale(1.05);
            }

            .placeholder-insert-btn.inserted {
                background: #2196F3;
            }

            .placeholder-copy-btn {
                flex-shrink: 0;
                padding: 8px 12px;
                background: #667eea;
                color: white;
                border: none;
                border-radius: 6px;
                cursor: pointer;
                font-size: 14px;
                transition: all 0.2s;
            }

            .placeholder-copy-btn:hover {
                background: #5568d3;
                transform: scale(1.05);
            }

            .placeholder-copy-btn.copied {
                background: #4caf50;
            }

            .placeholder-helper-footer {
                padding: 12px 20px;
                border-top: 1px solid #eee;
                background: #fafafa;
                font-size: 11px;
                color: #666;
                text-align: center;
                border-radius: 0 0 8px 8px;
            }

            .placeholder-helper-footer strong {
                color: #333;
            }

            /* Parameter Modal */
            .placeholder-param-modal {
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: rgba(0, 0, 0, 0.6);
                z-index: 10001;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 20px;
            }

            .placeholder-param-modal-content {
                background: white;
                border-radius: 8px;
                box-shadow: 0 8px 32px rgba(0,0,0,0.3);
                max-width: 500px;
                width: 100%;
                max-height: 90vh;
                overflow: auto;
            }

            .placeholder-param-modal-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 16px 20px;
                border-bottom: 1px solid #eee;
                background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
                color: white;
                border-radius: 8px 8px 0 0;
            }

            .placeholder-param-modal-header h4 {
                margin: 0;
                font-size: 16px;
                font-weight: 600;
            }

            .placeholder-param-close {
                background: none;
                border: none;
                color: white;
                font-size: 24px;
                cursor: pointer;
                padding: 0;
                width: 28px;
                height: 28px;
                line-height: 1;
                border-radius: 4px;
                transition: background 0.2s;
            }

            .placeholder-param-close:hover {
                background: rgba(255,255,255,0.2);
            }

            .placeholder-param-modal-body {
                padding: 20px;
            }

            .param-instruction {
                margin: 0 0 16px 0;
                font-size: 14px;
                color: #666;
            }

            #param-form {
                display: flex;
                flex-direction: column;
                gap: 16px;
            }

            .param-field {
                display: flex;
                flex-direction: column;
                gap: 6px;
            }

            .param-field label {
                font-size: 13px;
                font-weight: 600;
                color: #333;
            }

            .param-field input {
                padding: 10px 12px;
                border: 1px solid #ddd;
                border-radius: 6px;
                font-size: 14px;
                font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
                outline: none;
                transition: border-color 0.2s;
            }

            .param-field input:focus {
                border-color: #4caf50;
            }

            .param-field small {
                font-size: 11px;
                color: #999;
            }

            .param-field small code {
                background: #f6f8fa;
                padding: 2px 6px;
                border-radius: 3px;
                color: #d73a49;
            }

            .param-preview {
                margin-top: 16px;
                padding: 12px;
                background: #f9f9f9;
                border-radius: 6px;
                border: 1px solid #eee;
            }

            .param-preview label {
                display: block;
                font-size: 12px;
                font-weight: 600;
                color: #666;
                margin-bottom: 6px;
            }

            .param-preview code {
                display: block;
                font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
                font-size: 12px;
                color: #d73a49;
                background: white;
                padding: 8px;
                border-radius: 4px;
                word-break: break-all;
            }

            .placeholder-param-modal-footer {
                display: flex;
                justify-content: flex-end;
                gap: 10px;
                padding: 16px 20px;
                border-top: 1px solid #eee;
                background: #fafafa;
                border-radius: 0 0 8px 8px;
            }

            .param-cancel-btn {
                padding: 10px 20px;
                background: #fff;
                color: #666;
                border: 1px solid #ddd;
                border-radius: 6px;
                cursor: pointer;
                font-size: 14px;
                font-weight: 500;
                transition: all 0.2s;
            }

            .param-cancel-btn:hover {
                background: #f5f5f5;
                border-color: #ccc;
            }

            .param-insert-btn {
                padding: 10px 20px;
                background: #4caf50;
                color: white;
                border: none;
                border-radius: 6px;
                cursor: pointer;
                font-size: 14px;
                font-weight: 500;
                transition: all 0.2s;
            }

            .param-insert-btn:hover {
                background: #45a049;
            }

            /* Notification */
            .placeholder-notification {
                position: fixed;
                top: 20px;
                right: 20px;
                background: white;
                border-radius: 8px;
                box-shadow: 0 4px 16px rgba(0,0,0,0.15);
                padding: 16px 20px;
                display: flex;
                align-items: center;
                gap: 12px;
                z-index: 10002;
                opacity: 0;
                transform: translateX(400px);
                transition: all 0.3s ease;
            }

            .placeholder-notification.show {
                opacity: 1;
                transform: translateX(0);
            }

            .placeholder-notification i {
                font-size: 20px;
            }

            .placeholder-notification-success {
                border-left: 4px solid #4caf50;
            }

            .placeholder-notification-success i {
                color: #4caf50;
            }

            .placeholder-notification-error {
                border-left: 4px solid #f44336;
            }

            .placeholder-notification-error i {
                color: #f44336;
            }

            .placeholder-notification span {
                font-size: 14px;
                color: #333;
            }

            @media (max-width: 768px) {
                .placeholder-helper-panel {
                    width: calc(100% - 40px);
                    right: 20px;
                    left: 20px;
                }

                .placeholder-param-modal-content {
                    max-width: calc(100% - 40px);
                }

                .placeholder-actions {
                    flex-direction: column;
                    gap: 4px;
                }

                .placeholder-insert-btn,
                .placeholder-copy-btn {
                    width: 100%;
                }
            }
        `;

            // Safely append to head with validation
            if (document.head && style instanceof Node) {
                document.head.appendChild(style);
            } else {
                console.error('PlaceholderHelper: Cannot append style - invalid node or head missing');
            }
        } catch (error) {
            console.error('PlaceholderHelper: Error adding styles', error);
        }
    }
}
